/*
 *
 * DesktopNavBar
 *
 * @version 1.0.0
 * @author Davide Polci
 * @since 17-Oct-2024
 */

import React from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  AddressBook,
  CalendarCheckmark,
  CallPayment,
  ChevronDoubleLeft,
  ChevronDoubleRight,
  Home,
  LaptopSetting,
  LinkPayment,
  List,
  Location,
  NewCalendar,
  NewPayment,
  PaymentCheckmark,
  PaymentToCard,
  People,
  RecurrentPayment,
  ShoppingBag
} from '@nexidigital/iconlibrary'
import PortalIconButton from '@nexidigital/portaliconbutton'
import { useHandleSideBar } from 'Hoc/ListenerProvider'
import lineNavbar from 'Assets/svg/line_navbar.svg'
import { getLabels, getNavigationItems, navigationMessages } from 'Schemas/navigationMap'
import { routesList } from 'Schemas/routesList'
import './DesktopNavBar.scss'

const DesktopNavBar = ({ userPermissions, type, userProfile, getSubscriptionArray, actions }) => {
  const intl = useIntl()
  const { expanded, toggleSidebar } = useHandleSideBar()
  const visibilityContext = { userPermissions, getSubscriptionArray, userProfile, type }
  const visibleItems = getNavigationItems(visibilityContext)
  const isVisible = (id) => visibleItems.some((item) => item.id === id)

  const linkClassname = (isActive) =>
    `desktop_navbar_item ${isActive ? 'desktop_navbar_active_link' : 'desktop_navbar_link'}`
  const setPblDashboardSource = () => actions.doSetPblSourcePage?.('dashboard')
  const setBookingDashboardSource = () => actions.doSetBookingSourcePage?.('dashboard')

  const labels = getLabels(intl, navigationMessages)

  return (
    <nav
      className={`desktop_navbar_container ${
        expanded ? 'desktop_navbar_container_width_expanded' : 'desktop_navbar_container_width_collapsed'
      }`}>
      <div className='desktop_navbar_menutitle_container'>
        <h2 className='desktop_navbar_menutitle'>{labels.menu}</h2>
        <div className={`desktop_navbar_toggleIcon ${expanded ? 'expanded' : ''}`}>
          <PortalIconButton
            ariaLabel={`${expanded ? labels.close : labels.open} sidebar`}
            icon={expanded ? <ChevronDoubleLeft size='24' type='line' /> : <ChevronDoubleRight size='24' type='line' />}
            onClick={toggleSidebar}
          />
        </div>
      </div>

      <div className='desktop_navbar_menuitems_container' tabIndex='-1'>
        <div className='desktop_navbar_item_container'>
          <NavLink
            title={expanded ? '' : labels.home}
            to={routesList.goToDashboard}
            className={({ isActive }) => linkClassname(isActive)}>
            {({ isActive }) => (
              <>
                <span className='desktop_navbar_item_line' aria-hidden='true' />
                <Home size='24' type={isActive ? 'fill' : 'line'} />
                {expanded && <span className='desktop_navbar_item_label'>{labels.home}</span>}
              </>
            )}
          </NavLink>
        </div>

        {isVisible('customers') && (
          <div className='desktop_navbar_item_container'>
            <NavLink
              title={expanded ? '' : labels.customers}
              to={routesList.customers.goToCustomers}
              className={({ isActive }) => linkClassname(isActive)}>
              {({ isActive }) => (
                <>
                  <span className='desktop_navbar_item_line' aria-hidden='true' />
                  <AddressBook size='24' type={isActive ? 'fill' : 'line'} />
                  {expanded && <span className='desktop_navbar_item_label'>{labels.customers}</span>}
                </>
              )}
            </NavLink>
          </div>
        )}

        {(isVisible('payByLinkQuickAction') || isVisible('payToCardQuickAction') || isVisible('motoQuickAction') || isVisible('cardCheckQuickAction') || isVisible('bookingQuickAction')) && (
          <div className='desktop_navbar_menuitems_with_title_container'>
            <div className='desktop_navbar_title_container'>
              <p className='desktop_navbar_item_title'>{labels.quickActions}</p>
              <img src={lineNavbar} alt='' className='desktop_navbar_item_title_line' />
            </div>
            {isVisible('payByLinkQuickAction') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.quickActionPayByLink}
                  to={routesList.pblManagement.goToNewPbl}
                  onClick={setPblDashboardSource}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <NewPayment size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.quickActionPayByLink}</span>}
                    </>
                  )}
                </NavLink>
              </div>
              
            )}
            {isVisible('bookingQuickAction') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.newBooking}
                  to={routesList.bookingManagement.goToCreateBookingPayByLink}
                  state={{ bookingSourcePage: 'dashboard' }}
                  onClick={setBookingDashboardSource}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <NewCalendar size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.newBooking}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )} 
            {isVisible('motoQuickAction') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.moto}
                  to={routesList.motoRoutes.goToMoto}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <CallPayment size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.moto}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
            {isVisible('cardCheckQuickAction') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.cardCheck}
                  to={routesList.cardCheck.goToCardCheck}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <PaymentCheckmark size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.cardCheck}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
            {isVisible('payToCardQuickAction') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.quickActionPayToCard}
                  to={routesList.payToCard.goToPayToCard}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <PaymentToCard size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.quickActionPayToCard}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}

        {(isVisible('orders') || isVisible('operations') || isVisible('subscriptions')) && (
          <div className='desktop_navbar_menuitems_with_title_container'>
            <div className='desktop_navbar_title_container'>
              <p className='desktop_navbar_item_title'>{labels.payments}</p>
              <img src={lineNavbar} alt='' className='desktop_navbar_item_title_line' />
            </div>
            {isVisible('orders') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.ordersManagement}
                  to={routesList.orderManagementRoutes.goToOrderManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <ShoppingBag size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.ordersManagement}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('operations') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.operationsManagement}
                  to={routesList.operationManagement.goToOperationManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <List size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.operationsManagement}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}

            {isVisible('subscriptions') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.subscriptionManagement}
                  to={routesList.subscriptionManagementRoutes.goToSubscriptionPlan}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <RecurrentPayment size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.subscriptionManagement}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}

        {(isVisible('storeManagement') || isVisible('terminalsManagement') || isVisible('usersManagement')) && (
          <div className='desktop_navbar_menuitems_with_title_container'>
            <div className='desktop_navbar_title_container'>
              <p className='desktop_navbar_item_title'>{labels.configuration}</p>
              <img src={lineNavbar} alt='' className='desktop_navbar_item_title_line' />
            </div>
            {isVisible('storeManagement') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.stores}
                  to={routesList.pointsOfSales.goToPointsOfSales}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <Location size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.stores}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
            {isVisible('terminalsManagement') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.manageTerminals}
                  to={routesList.terminalsManagement.goToTerminalsManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <LaptopSetting size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.manageTerminals}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
            {isVisible('usersManagement') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.users}
                  to={routesList.users.goToUsers}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <People size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.users}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}

        {(isVisible('payByLink') || isVisible('bookingManagement')) && (
          <div className='desktop_navbar_menuitems_with_title_container'>
            <div className='desktop_navbar_title_container'>
              <p className='desktop_navbar_item_title'>{labels.services}</p>
              <img src={lineNavbar} alt='' className='desktop_navbar_item_title_line' />
            </div>
            {isVisible('payByLink') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.payByLink}
                  to={routesList.pblManagement.goToPblManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <LinkPayment size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.payByLink}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
            {isVisible('bookingManagement') && (
              <div className='desktop_navbar_item_container'>
                <NavLink
                  title={expanded ? '' : labels.bookingManagement}
                  to={routesList.bookingManagement.goToBookingManagement}
                  className={({ isActive }) => linkClassname(isActive)}>
                  {({ isActive }) => (
                    <>
                      <span className='desktop_navbar_item_line' aria-hidden='true' />
                      <CalendarCheckmark size='24' type={isActive ? 'fill' : 'line'} />
                      {expanded && <span className='desktop_navbar_item_label'>{labels.bookingManagement}</span>}
                    </>
                  )}
                </NavLink>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  )
}

DesktopNavBar.propTypes = {
  userPermissions: PropTypes.array,
  type: PropTypes.string,
  userProfile: PropTypes.object,
  getSubscriptionArray: PropTypes.array,
  actions: PropTypes.object
}

DesktopNavBar.defaultProps = {
  userPermissions: [],
  type: '',
  userProfile: {},
  getSubscriptionArray: [],
  actions: {}
}

export default DesktopNavBar
